import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import RichTextEditor from './RichTextEditor';
import { hasActualTextContent } from '../utils/sanitize';

interface TimeEntryRejectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (remarks: string, actionRequired: string) => void;
  isLoading?: boolean;
  entrySummary?: {
    workedDate: string;
    userName: string;
    type: string;
    timeSpent: string;
    customer: string;
    engagement: string;
  };
  title?: string;
}

const TimeEntryRejectionModal: React.FC<TimeEntryRejectionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  entrySummary,
  title = 'Reject Time Entry',
}) => {
  const [remarks, setRemarks] = useState('');
  const [actionRequired, setActionRequired] = useState('');
  const [remarksError, setRemarksError] = useState('');
  const [actionError, setActionError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setRemarks('');
      setActionRequired('');
      setRemarksError('');
      setActionError('');
    }
  }, [isOpen]);

  const handleConfirm = () => {
    let hasError = false;

    if (!hasActualTextContent(remarks)) {
      setRemarksError('Rejection remarks are required');
      hasError = true;
    }

    if (!hasActualTextContent(actionRequired)) {
      setActionError('Action required is mandatory');
      hasError = true;
    }

    if (hasError) return;

    onConfirm(remarks, actionRequired);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                        {title}
                      </Dialog.Title>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mt-2 ml-9">
                  {entrySummary && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Entry Details</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-500">User:</span>{' '}
                          <span className="text-gray-900">{entrySummary.userName}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Date:</span>{' '}
                          <span className="text-gray-900">{entrySummary.workedDate}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Customer:</span>{' '}
                          <span className="text-gray-900">{entrySummary.customer}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Engagement:</span>{' '}
                          <span className="text-gray-900">{entrySummary.engagement}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Type:</span>{' '}
                          <span className="text-gray-900">{entrySummary.type}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Time:</span>{' '}
                          <span className="text-gray-900 font-medium">{entrySummary.timeSpent}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-sm text-gray-500 mb-4">
                    Please provide the reason for rejection and specify the action required from the user.
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Remarks <span className="text-red-500">*</span>
                    </label>
                    <RichTextEditor
                      value={remarks}
                      onChange={(value) => {
                        setRemarks(value);
                        setRemarksError('');
                      }}
                      placeholder="Enter the reason for rejecting this time entry..."
                      minHeight="120px"
                      disabled={isLoading}
                      error={!!remarksError}
                    />
                    {remarksError && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {remarksError}
                      </p>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Action Required <span className="text-red-500">*</span>
                    </label>
                    <RichTextEditor
                      value={actionRequired}
                      onChange={(value) => {
                        setActionRequired(value);
                        setActionError('');
                      }}
                      placeholder="Specify what action the user needs to take..."
                      minHeight="120px"
                      disabled={isLoading}
                      error={!!actionError}
                    />
                    {actionError && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        {actionError}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-colors"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Rejecting...' : 'Reject'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TimeEntryRejectionModal;
